import React, { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, IconButton, Skeleton,
} from "@mui/material";
import { FaEdit, FaTrash, FaPlus, FaEye, FaEyeSlash } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { MdOutlineDelete } from "react-icons/md";
import { toast } from "react-toastify";
import {
  getSubAdmins, createSubAdmin, updateSubAdmin, deleteSubAdmin,
} from "../../Redux/Actions/SubAdminAction";
import useTitle from "../../Hooks/useTitle";
import useScrollToTop from "../../Hooks/useScrollToTop";
import CustomPagination from "../../components/FormComponents/CustomPagination";

const EMPTY_FORM = { firstname: "", lastname: "", email: "", password: "" };

const SubAdminManagement = () => {
  useTitle("Sub-Admin Management");
  useScrollToTop();

  const dispatch = useDispatch();
  const [subAdmins, setSubAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Modal state
  const [modal, setModal] = useState({ open: false, mode: "create", data: null });
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formLoading, setFormLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Delete confirm dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: "" });

  const fetchSubAdmins = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await dispatch(getSubAdmins(page));
      const payload = res?.data?.data ?? res?.data;
      if (payload && typeof payload === "object" && !Array.isArray(payload)) {
        setSubAdmins(Array.isArray(payload.data) ? payload.data : []);
        setTotalPages(payload.last_page || 1);
      } else {
        setSubAdmins(Array.isArray(payload) ? payload : []);
        setTotalPages(1);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to fetch sub-admins");
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchSubAdmins(currentPage);
  }, [fetchSubAdmins, currentPage]);

  const openCreateModal = () => {
    setFormData(EMPTY_FORM);
    setModal({ open: true, mode: "create", data: null });
  };

  const openEditModal = (subAdmin) => {
    setFormData({
      firstname: subAdmin.firstname || "",
      lastname: subAdmin.lastname || "",
      email: subAdmin.email || "",
      password: "",
    });
    setModal({ open: true, mode: "edit", data: subAdmin });
  };

  const closeModal = () => {
    setModal({ open: false, mode: "create", data: null });
    setFormData(EMPTY_FORM);
    setShowPassword(false);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      if (modal.mode === "create") {
        await dispatch(createSubAdmin(formData));
        toast.success("Sub-admin created successfully");
      } else {
        // Only send changed/non-empty fields for update
        const payload = {};
        if (formData.firstname) payload.firstname = formData.firstname;
        if (formData.lastname) payload.lastname = formData.lastname;
        if (formData.email) payload.email = formData.email;
        if (formData.password) payload.password = formData.password;
        await dispatch(updateSubAdmin(modal.data.id, payload));
        toast.success("Sub-admin updated successfully");
      }
      closeModal();
      fetchSubAdmins(currentPage);
    } catch (error) {
      // Show field-level Laravel validation errors if present
      const errors = error?.response?.data?.errors;
      if (errors && typeof errors === "object") {
        Object.values(errors).flat().forEach((msg) => toast.error(msg));
      } else {
        toast.error(error?.response?.data?.message || "Operation failed");
      }
    } finally {
      setFormLoading(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await dispatch(deleteSubAdmin(deleteDialog.id));
      toast.success("Sub-admin deleted successfully");
      setDeleteDialog({ open: false, id: null, name: "" });
      fetchSubAdmins(currentPage);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete sub-admin");
    }
  };

  return (
    <div className="md:max-w-[750px] lg:max-w-[1100px] xl:max-w-[1200px] mx-auto px-4 py-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Sub-Admin Management</h2>
            <p className="text-gray-500 text-sm mt-1">Create and manage sub-administrator accounts</p>
          </div>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-4 py-2 bg-[#E72848] text-white rounded-lg hover:bg-[#d62040] transition-colors font-medium text-sm"
          >
            <FaPlus size={13} />
            Add Sub-Admin
          </button>
        </div>
      </div>

      {/* Table */}
      <Paper sx={{ borderRadius: "12px", boxShadow: "0px 2px 12px 0px rgba(0,0,0,0.06)", border: "1px solid #f0f0f0", overflow: "hidden" }}>
        <TableContainer>
          <Table aria-label="sub-admins table">
            <TableHead>
              <TableRow>
                {["#", "Name", "Email", "Actions"].map((col) => (
                  <TableCell
                    key={col}
                    sx={{ backgroundColor: "#f9fafb", fontWeight: "600", fontSize: "14px", color: "#4b5563", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: "1px solid #e5e7eb", py: 2 }}
                  >
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 4 }).map((__, j) => (
                      <TableCell key={j}><Skeleton variant="text" width="80%" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : subAdmins.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6, color: "#9ca3af", fontSize: "15px" }}>
                    No sub-admins found. Click "Add Sub-Admin" to create one.
                  </TableCell>
                </TableRow>
              ) : (
                subAdmins.map((sa, index) => (
                  <TableRow key={sa.id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                    <TableCell sx={{ color: "#6b7280", fontSize: "14px", py: 1.5 }}>{index + 1}</TableCell>
                    <TableCell sx={{ fontWeight: "500", color: "#111827", fontSize: "14px", py: 1.5 }}>
                      {sa.firstname} {sa.lastname}
                    </TableCell>
                    <TableCell sx={{ color: "#6b7280", fontSize: "14px", py: 1.5 }}>{sa.email}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <div className="flex items-center gap-1">
                        <IconButton size="small" onClick={() => openEditModal(sa)} title="Edit" sx={{ color: "#3b82f6", "&:hover": { backgroundColor: "#eff6ff" } }}>
                          <FaEdit size={16} />
                        </IconButton>
                        <IconButton size="small" onClick={() => setDeleteDialog({ open: true, id: sa.id, name: `${sa.firstname} ${sa.lastname}` })} title="Delete" sx={{ color: "#ef4444", "&:hover": { backgroundColor: "#fef2f2" } }}>
                          <FaTrash size={16} />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {totalPages > 1 && (
        <div className="flex justify-center mt-6">
          <CustomPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* Create / Edit Modal */}
      {modal.open && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50 p-4" onClick={closeModal}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-lg font-semibold text-gray-900">
                {modal.mode === "create" ? "Add Sub-Admin" : "Edit Sub-Admin"}
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <IoMdClose size={22} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstname" className="block text-base font-medium text-gray-700 mb-1">First Name</label>
                  <input
                    id="firstname"
                    type="text"
                    name="firstname"
                    value={formData.firstname}
                    onChange={handleFormChange}
                    required={modal.mode === "create"}
                    placeholder="First name"
                    className="w-full border border-gray-300 rounded-lg p-3 text-base focus:outline-none focus:ring-2 focus:ring-[#E72848] focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="lastname" className="block text-base font-medium text-gray-700 mb-1">Last Name</label>
                  <input
                    id="lastname"
                    type="text"
                    name="lastname"
                    value={formData.lastname}
                    onChange={handleFormChange}
                    required={modal.mode === "create"}
                    placeholder="Last name"
                    className="w-full border border-gray-300 rounded-lg p-3 text-base focus:outline-none focus:ring-2 focus:ring-[#E72848] focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-base font-medium text-gray-700 mb-1">Email</label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  required={modal.mode === "create"}
                  placeholder="email@example.com"
                  className="w-full border border-gray-300 rounded-lg p-3 text-base focus:outline-none focus:ring-2 focus:ring-[#E72848] focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-base font-medium text-gray-700 mb-1">
                  Password {modal.mode === "edit" && <span className="text-gray-400 font-normal text-sm">(leave blank to keep current)</span>}
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleFormChange}
                    required={modal.mode === "create"}
                    placeholder={modal.mode === "edit" ? "Leave blank to keep current" : "Password"}
                    className="w-full border border-gray-300 rounded-lg p-3 pr-11 text-base focus:outline-none focus:ring-2 focus:ring-[#E72848] focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                  </button>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeModal} className="flex-1 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium text-sm hover:bg-gray-50 transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={formLoading} className="flex-1 py-2.5 bg-[#E72848] text-white rounded-lg font-medium text-sm hover:bg-[#d62040] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                  {formLoading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                  {modal.mode === "create" ? "Create" : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm Dialog */}
      {deleteDialog.open && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50 p-4" onClick={() => setDeleteDialog({ open: false, id: null, name: "" })}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-xs p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end">
              <IoMdClose size={20} className="cursor-pointer text-gray-400" onClick={() => setDeleteDialog({ open: false, id: null, name: "" })} />
            </div>
            <div className="flex justify-center mt-2">
              <div className="border-2 border-[#e72848] rounded-full p-4">
                <MdOutlineDelete size={30} color="#e72848" />
              </div>
            </div>
            <div className="px-2 py-5 flex flex-col items-center text-center">
              <p className="text-xl font-bold">Are you sure?</p>
              <p className="text-gray-500 text-sm mt-1">Do you really want to delete <span className="font-medium text-gray-700">{deleteDialog.name}</span>? This cannot be undone.</p>
            </div>
            <div className="flex gap-3">
              <button className="flex-1 py-2 border border-gray-300 rounded-xl text-sm" onClick={() => setDeleteDialog({ open: false, id: null, name: "" })}>
                Cancel
              </button>
              <button className="flex-1 py-2 bg-[#e72848] text-white rounded-xl text-sm hover:bg-[#b91c1c] transition-colors" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubAdminManagement;
